import React from 'react'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { BCol, BForm, BRow } from '@prague-digi/ui-components'
import get from 'lodash/get'

import {
  AddressInputs,
  BAutoComplete,
  BCheckbox,
  BTextInput,
  PageControlButtons,
  PrefillFormButton,
  Title,
} from '../../components'
import { ReduxState } from '../../types'
import messages from '../../i18n'
import {
  errorHandler,
  form,
  FormInterface,
  intl,
  IntlInterface,
  wizard,
  WizardInterface,
} from '../../hoc'
import formValidate from './FinanceEmployer.validate'
import { modalDialogActions, clientActions, codetableActions } from '../../redux'
import { getOpenApiInstance, ApiErrorInfo, Response } from '../../api/api'
import { API_ERROR } from '../../constants/modals'
import { preformatZip, trim } from '../../utils/helpers'
import { unixTimeToCzDate } from '../../utils/dates'
import { ErrSuccess } from '../../types/form'
import { EMPLOYMENT_TYPES } from '../../constants'

interface Props extends FormInterface, IntlInterface, WizardInterface {
  isModal?: boolean
  onClose: any
  onContinue: any
  employmentTypeCode: string
  getAddressHints: any
  saveFinanceEmployer: (callbacks: ErrSuccess) => void
  showModalDialog: any
  storedData: any
}

interface Address {
  county: string
  descriptiveNumber: number
  name: string
  orientationNumber: string
  postCode: string
  street: string
  town: string
  zip: string
}

interface State {
  employers: Employer[]
  searchedEmployerText: string
}

interface Employer extends Address {
  ico: string
}

const FORM_NAME = 'FinanceEmployer'

export class FinanceEmployer extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    // Init form values
    props.initialize(props.storedData)
  }

  state: State = {
    employers: [],
    searchedEmployerText: '',
  }

  onSubmit = () => {
    this.props.handleSubmit(this.saveFinanceEmployerCallback)()
  }

  onBack = () => {
    this.props.wizard.gotoPrevious()
  }

  saveFinanceEmployerCallback = () => {
    this.props.saveFinanceEmployer({
      success: () => {
        if (this.props.onContinue) {
          this.props.onContinue()
        } else {
          this.props.wizard.gotoNext()
        }
      },
      error: (errorInfo: ApiErrorInfo) => {
        if (errorInfo.type === Response.dataError && errorInfo.errors) {
          this.props.setErrors(FORM_NAME, errorInfo.errors)
        } else {
          this.props.showModalDialog(API_ERROR({ errorInfo, onRetry: this.onSubmit }))
        }
      },
    })
  }

  fetchEmployers = (value: any) => {
    const fetchEmployersRequestId = Math.random()
    this.fetchEmployersRequestId = fetchEmployersRequestId
    clearTimeout(this.fetchEmployersTimeoutRef)
    this.fetchEmployersTimeoutRef = setTimeout(() => {
      if (value) {
        if (value !== this.state.searchedEmployerText) {
          getOpenApiInstance()
            .getEmployerHints({ companyNamePart: value })
            .then((response: any) => {
              if (this.fetchEmployersRequestId === fetchEmployersRequestId) {
                let searchedEmployerText: string = ''
                let employers: Employer[] = []

                if (response.responseStatus === Response.ok) {
                  searchedEmployerText = value
                  employers = get(response, 'data.companies', []).map((item: any) => ({
                    ...item.address,
                    ico: item.ico,
                  }))
                }

                this.setState({
                  searchedEmployerText,
                  employers,
                })
              }
            })
        }
      } else {
        this.setState({ employers: [] })
      }
    }, 300)
  }

  employerClearRequested = () => {
    clearTimeout(this.fetchEmployersTimeoutRef)
    this.setState({
      searchedEmployerText: '',
      employers: [],
    })
  }

  employerHintSelected = ({ ico, ...address }: Employer) => {
    this.props.change('employerAddress.landRegistryNumber', trim(address.descriptiveNumber))
    this.props.change('employerAddress.houseNumber', trim(address.orientationNumber))
    this.props.change('employerAddress.street', trim(address.street))
    this.props.change('employerAddress.city', trim(address.town))
    this.props.change('employerAddress.zip', trim(address.zip))
    this.props.change('employerName', address.name)
    this.props.change('employerId', trim(ico))
  }

  fetchEmployersTimeoutRef: any
  fetchEmployersRequestId: any

  render() {
    const { isModal, employmentTypeCode, invalid, translate } = this.props

    return (
      <BForm id={FORM_NAME}>
        <Title center={isModal} message={messages.financeEmployer.title} subTitle={!isModal} />

        <BRow>
          <BCol xs={12}>
            <BAutoComplete
              testid="employerName"
              name="employerName"
              messages={{
                label: translate(messages.financeEmployer.employer),
              }}
              onSuggestionsFetchRequested={this.fetchEmployers}
              onSuggestionSelected={this.employerHintSelected}
              onSuggestionsClearRequested={this.employerClearRequested}
              data={this.state.employers}
            />
          </BCol>
        </BRow>

        <BRow>
          <BCol xl={6} lg={6} md={12} sm={12} xs={12}>
            <BTextInput
              testid="employerId"
              name="employerId"
              messages={{
                label: translate(messages.financeEmployer.identificationNumber),
              }}
            />
          </BCol>
        </BRow>

        <AddressInputs form={FORM_NAME} namePrefix="employerAddress" />

        <BRow>
          <BCol xl={6} lg={6} md={12} sm={12} xs={12}>
            <BTextInput
              testid="employedSince"
              name="employedSince"
              messages={{
                label: translate(messages.financeEmployer.employedFrom),
              }}
            />
          </BCol>
          {employmentTypeCode === EMPLOYMENT_TYPES.na_dobu_urcitou && (
            <BCol xl={6} lg={6} md={12} sm={12} xs={12}>
              <BTextInput
                testid="employedTo"
                name="employedTo"
                messages={{
                  label: translate(messages.financeEmployer.employedTo),
                }}
              />
            </BCol>
          )}
        </BRow>

        <BRow>
          <BCol xs={12}>
            <BCheckbox
              testid="trialPeriod"
              name="trialPeriod"
              message={translate(messages.financeEmployer.employmentTrial)}
            />
          </BCol>
        </BRow>
        <BRow>
          <BCol xs={12}>
            <BCheckbox
              testid="noticePeriod"
              name="noticePeriod"
              message={translate(messages.financeEmployer.employmentNotice)}
            />
          </BCol>
        </BRow>

        <PageControlButtons
          disableSubmit={invalid}
          onSubmit={this.onSubmit}
          secondaryAction={isModal ? undefined : this.onBack} // it can`t be closed in modal view
        />
        <PrefillFormButton formName="FinanceEmployer" />
      </BForm>
    )
  }
}

export default compose(
  errorHandler('containers/FinanceEmployer/FinanceEmployer.tsx'),
  wizard,
  connect(
    (state: ReduxState) => ({
      storedData: {
        employerAddress: {
          city: state.client.employerAddress.city,
          houseNumber: state.client.employerAddress.houseNumber,
          landRegistryNumber: state.client.employerAddress.landRegistryNumber,
          street: state.client.employerAddress.street,
          zip: preformatZip(state.client.employerAddress.zip),
        },
        employerName: state.client.employerName,
        employerId: state.client.employerId,
        employedSince: unixTimeToCzDate(state.client.employedSince),
        employedTo: unixTimeToCzDate(state.client.employedTo),
        trialPeriod: state.client.trialPeriod,
        noticePeriod: state.client.noticePeriod,
      },
      employmentTypeCode: state.client.employmentTypeCode,
    }),
    {
      saveFinanceEmployer: clientActions.saveFinanceEmployer,
      showModalDialog: modalDialogActions.showModalDialog,
      getAddressHints: codetableActions.getAddressHints,
    }
  ),
  form({
    name: FORM_NAME,
    validate: formValidate,
  }),
  intl
)(FinanceEmployer)
